package com.shadowings.gero.data.lessons.chapter15

import com.shadowings.gero.data.lessons.inlineMeaning
import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.lessons.buildVocabQuizContent
import com.shadowings.gero.data.lessons.chapter12.buildMultiPress
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.ExplanationContent
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.IExplanation
import com.shadowings.gero.domain.model.IQuizContent
import com.shadowings.gero.presentation.component.MdCWord
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.exp_content_nomi_e_aggettivi_1_1
import gero.composeapp.generated.resources.exp_content_nomi_e_aggettivi_1_2
import gero.composeapp.generated.resources.exp_content_nomi_e_aggettivi_1_3
import gero.composeapp.generated.resources.exp_content_word_reading_and_meaning
import gero.composeapp.generated.resources.exp_title_nomi_e_aggettivi_1
import gero.composeapp.generated.resources.exp_title_word_name
import kotlin.random.Random

val nomiEAggettivi1WordIds = listOf("SEGUACE", "ECCELLENTE", "CASA_DIMORA", "TUTTO", "BUONO", "GRANDE", "PICCOLO")

// Already-taught words, used as distractors and as review filler.
private val nomiEAggettivi1OldWordIds = listOf("CASA", "TEMPIO", "UOMO", "DIO", "SCRIBA", "FIGLIO")

fun getNomiEAggettivi1Content(words: List<Word>, languageCode: String): List<IExplanation> {
    val intro = listOf(
        ExplanationContent(
            title = Res.string.exp_title_nomi_e_aggettivi_1,
            content = Res.string.exp_content_nomi_e_aggettivi_1_1,
            face = Faces.Perfect,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_nomi_e_aggettivi_1,
            content = Res.string.exp_content_nomi_e_aggettivi_1_2,
            face = Faces.Happy,
            character = Characters.Zeta,
        ),
        ExplanationContent(
            title = Res.string.exp_title_nomi_e_aggettivi_1,
            content = Res.string.exp_content_nomi_e_aggettivi_1_3,
            face = Faces.Happy,
            character = Characters.Nilo,
        )
    )

    val slides = nomiEAggettivi1WordIds
        .mapNotNull { id -> words.firstOrNull { it.id == id } }
        .map { word ->
            ExplanationContent(
                title = Res.string.exp_title_word_name,
                titleArgs = listOf(word.getMeaning(languageCode)),
                content = Res.string.exp_content_word_reading_and_meaning,
                contentArgs = listOf(
                    word.reading,
                    word.inlineMeaning(languageCode),
                    word.getNote(languageCode)
                ),
                face = Faces.Happy,
                character = Characters.Alyn,
                composable = { MdCWord(word.gardiner, rtl = false, cart = false, size = 96.dp) },
                wordId = word.id
            )
        }

    return intro + slides
}

// One question of a random type per new word + a multipress on 4 new words
// + review filler: a multipress mixing new and old words and one old-word question.
fun getNomiEAggettivi1QuizContent(
    words: List<Word>,
    languageCode: String,
    random: Random = Random
): List<IQuizContent> {
    val explanations = getNomiEAggettivi1Content(words, languageCode)
    val optionIds = (nomiEAggettivi1WordIds + nomiEAggettivi1OldWordIds).toSet()

    val newWordQuestions = explanations
        .filterIsInstance<ExplanationContent>()
        .filter { it.wordId != null }
        .mapNotNull { slide ->
            buildVocabQuizContent(
                explanations = listOf(slide),
                words = words,
                languageCode = languageCode,
                additionalOptions = optionIds
            ).randomOrNull(random)
        }

    val newWords = nomiEAggettivi1WordIds.mapNotNull { id -> words.firstOrNull { it.id == id } }
    val oldWords = nomiEAggettivi1OldWordIds.mapNotNull { id -> words.firstOrNull { it.id == id } }

    val multiPressNew = buildMultiPress(newWords.shuffled(random).take(4), languageCode)
    val multiPressMixed = buildMultiPress(
        (newWords.shuffled(random).take(2) + oldWords.shuffled(random).take(2)).shuffled(random),
        languageCode
    )

    val oldWordReview = buildVocabQuizContent(
        explanations = getOldWordSlides(oldWords.shuffled(random).take(1)),
        words = words,
        languageCode = languageCode,
        additionalOptions = optionIds
    ).randomOrNull(random)

    return (newWordQuestions + multiPressNew + multiPressMixed + listOfNotNull(oldWordReview)).shuffled(random)
}

// buildVocabQuizContent only reads the wordId of its explanations.
private fun getOldWordSlides(oldWords: List<Word>): List<IExplanation> =
    oldWords.map { word ->
        ExplanationContent(
            title = Res.string.exp_title_word_name,
            content = Res.string.exp_content_word_reading_and_meaning,
            face = Faces.Happy,
            wordId = word.id
        )
    }
